import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';
import i18n from '../../../../../../constants/initLng';
import { Unlink } from './../icons'
import { compact } from 'lodash';
/**
 * The simple button plugin. It makes buttons in the toolbar configurable from outside.
 *
 * @extends module:core/plugin~Plugin
 */
export default class ImageLink extends Plugin {

    /**
     * @inheritDoc
     */
    static get requires() {
        return [ImageLinkUi];
    }

    /**
     * @inheritDoc
     */
    static get pluginName() {
        return 'ImageLink';
    }

}
export class ImageLinkUi extends Plugin {

    init() {
        const editor = this.editor;
        editor.model.schema.extend('image', { allowAttributes: ['linkHref'] });
        editor.conversion.for('upcast').add(upcastLink());
        editor.conversion.for('upcast').add(upcastImageLink('img'));
        editor.conversion.for('upcast').add(upcastImageLink('figure'));

        editor.conversion.for('editingDowncast').add(downcastImageLink());
        editor.conversion.for('dataDowncast').add(downcastImageLinkData());
        editor.ui.componentFactory.add('unlinkImage', locale => {
            const button = new ButtonView(locale);
            // const linkCommand = editor.commands.get('link');
            button.set({
                label: i18n.t(`action.unlink`),
                icon: Unlink,
                withText: false,
                tooltip: true
            });

            button.on('execute', () => {
                editor.model.change(writer => {
                    const imageView = editor.model.document.selection.getSelectedElement();
                    const isImageLinked = imageView && imageView.name && imageView.name === 'image'
                        && imageView.getAttribute && imageView.getAttribute('linkHref');
                    if (isImageLinked) {
                        button.isVisible = false;
                        editor.execute('unlink');
                        const viewRangeOnImage = writer.createRangeOn(imageView);
                        const attrs = {};
                        imageView._attrs.forEach((value, key, map) => {
                            if (key !== 'linkHref' && key !== 'linkAddTargetToExternalLinks' && key !== 'linkToggleDownloadable') {
                                attrs[key] = value;
                            }
                        })
                        const newImageView = writer.createElement('image', attrs);
                        writer.remove(imageView);
                        writer.insert(newImageView, viewRangeOnImage.start);
                        imageView._children._nodes.forEach(child => writer.append(child, newImageView));
                        const viewRangeOnNewImage = writer.createRangeOn(newImageView);
                        writer.setSelection(viewRangeOnNewImage);
                    }
                });
            });

            editor.model.document.selection.on('change', () => {
                const imageView = editor.model.document.selection.getSelectedElement();

                const href = imageView && imageView.name && imageView.name === 'image'
                    && imageView.getAttribute && imageView.getAttribute('linkHref');
                button.isVisible = !!href;
            })
            return button;
        });
    }

}


/**
 * Returns converter for links that wraps <img> or <figure> elements.
 *
 * @returns {Function}
 */
function upcastLink() {


    return dispatcher => {
        dispatcher.on('element:a', (evt, data, conversionApi) => {
            const viewLink = data.viewItem;
            const imageInLink = Array.from(viewLink.getChildren()).find(child => child.name === 'img' || child.name === 'figure');
            if (imageInLink) {
                // There's an image (or figure) inside an <a> element - we consume it so it won't be picked up by Link plugin.
                const consumableAttributes = { attributes: ['linkHref', 'href'] };

                // Consume the link so the default one will not convert it to $text attribute.
                if (!conversionApi.consumable.test(viewLink, consumableAttributes)) {
                    // Might be consumed by something else - ie other converter with priority=highest - a standard check.
                    return;
                }

                // Consume 'linkHref' attribute from link element.
                conversionApi.consumable.consume(viewLink, consumableAttributes);
            }
        }, { priority: 'high' });
    };
}

function upcastImageLink(elementName) {
    return dispatcher => {
        dispatcher.on(`element:${elementName}`, (evt, data, conversionApi) => {
            const viewImage = data.viewItem;
            const parent = viewImage.parent;

            // Check only <img>/<figure> that are direct children of a link.
            if (parent.name === 'a') {
                const modelImage = Array.from(data.modelRange.getItems()).find(item => item.is('image'));
                const linkHref = parent.getAttribute('linkHref') || parent.getAttribute('href');
                const target = parent._attrs.get('target');
                const downloadable = parent._attrs.get('download');
                if (modelImage && linkHref) {
                    // Set the linkHref attribute from link element on model image element.
                    conversionApi.writer.setAttribute('linkHref', linkHref, modelImage);
                    if (target) {
                        conversionApi.writer.setAttribute('linkAddTargetToExternalLinks', target, modelImage);
                    }
                    if (downloadable) {
                        conversionApi.writer.setAttribute('linkToggleDownloadable', downloadable, modelImage);
                    }
                }
            }
        }, { priority: 'normal' });
    };
}

function downcastImageLink() {
    return dispatcher => {
        dispatcher.on('attribute:linkHref:image', (evt, data, conversionApi) => {
            const linkHref = data.attributeNewValue;
            // The image will be already converted - so it will be present in the view.
            const target = data.item._attrs.get('linkAddTargetToExternalLinks') ? '_blank' : undefined;
            const download = data.item._attrs.get('linkToggleDownloadable') ? 'file' : undefined;
            const imageElm = conversionApi.mapper.toViewElement(data.item);
            const viewImage = Array.from(imageElm.getChildren()).find(child => child.name === 'img')
            const parent = imageElm.parent;
            if (linkHref && viewImage && (parent.name !== 'a' || parent.getAttribute('linkHref') !== linkHref)) {
                // Below will wrap already converted image by newly created link element.

                // 1. Create empty link element.
                const linkElement = conversionApi.writer.createContainerElement('a', getLinkAttrs({
                    linkHref, title: linkHref,
                    class: 'image',
                    download, target,
                    rel: target && 'noopener noreferrer',
                }), { priority: 5 });
                conversionApi.writer.setCustomProperty('link', true, linkElement)
                // 2. Insert link before associated image.
                // conversionApi.writer.insert(Position.createBefore(viewImage), linkElement);
                const viewRangeOnImage = conversionApi.writer.createRangeOn(viewImage);
                conversionApi.writer.insert(viewRangeOnImage.end, linkElement);
                const linkPosition = conversionApi.writer.createRangeIn(linkElement);
                // 3. Move whole converted image to a link.
                conversionApi.writer.move(conversionApi.writer.createRangeOn(viewImage), linkPosition.start);
            }

        }, { priority: 'normal' });
    };
}

function downcastImageLinkData() {
    return dispatcher => {
        dispatcher.on('attribute:linkHref:image', (evt, data, conversionApi) => {
            const href = data.attributeNewValue;
            // The image will be already converted - so it will be present in the view.
            const target = data.item._attrs.get('linkAddTargetToExternalLinks') ? '_blank' : undefined;
            const download = data.item._attrs.get('linkToggleDownloadable') ? 'file' : undefined;
            const imageElm = conversionApi.mapper.toViewElement(data.item);
            const viewImage = Array.from(imageElm.getChildren()).find(child => child.name === 'img')
            const parent = imageElm.parent;
            if (href && viewImage && (parent.name !== 'a' || parent.getAttribute('href') !== href)) {
                // Below will wrap already converted image by newly created link element.

                // 1. Create empty link element.
                const linkElement = conversionApi.writer.createContainerElement('a', getLinkAttrs({
                    href, class: 'image',
                    download, target,
                    rel: target && 'noopener noreferrer',
                }), { priority: 5 });
                conversionApi.writer.setCustomProperty('link', true, linkElement)
                // 2. Insert link before associated image.
                // conversionApi.writer.insert(Position.createBefore(viewImage), linkElement);
                const viewRangeOnImage = conversionApi.writer.createRangeOn(viewImage);
                conversionApi.writer.insert(viewRangeOnImage.end, linkElement);
                const linkPosition = conversionApi.writer.createRangeIn(linkElement);
                // 3. Move whole converted image to a link.
                conversionApi.writer.move(conversionApi.writer.createRangeOn(viewImage), linkPosition.start);
            }

        }, { priority: 'normal' });
    };
}


function getLinkAttrs(attrs = {}) {
    const result = {};
    Object.keys(attrs).forEach(key => {
        if (attrs[key]) {
            result[key] = attrs[key];
        }
    });
    return result;
}